/**
 * A listing in full.
 *
 * Three things are never buried: the total paid, the salary range, and the
 * link out to the company's own posting.
 */

import { Link, useParams } from 'react-router-dom';
import type { ReactNode } from 'react';

import {
  COMMON_BENEFITS,
  LISTING_LIFETIME_DAYS,
  VISA_SPONSORSHIP_LABELS,
  getCategory,
  getSubcategory,
  type JobDetail,
} from '@bidtohire/shared';

import { ApplyButton } from '../components/ApplyButton';
import { BidSparkline } from '../components/BidSparkline';
import { CompanyCard } from '../components/CompanyCard';
import { Money } from '../components/Money';
import { ShareRow } from '../components/ShareRow';
import { BoardSkeleton, ErrorNotice, MissingState, isMissing } from '../components/Status';
import { LeadCaret } from '../components/ui';
import {
  compensationLabel,
  daysRemaining,
  employmentLabel,
  formatDate,
  formatShortDate,
  isExpired,
  locationLabel,
  salaryFiguresOnly,
  salaryPeriodOnly,
  seniorityLabel,
  timeAgo,
  workplaceLabel,
} from '../lib/format';
import { useJob } from '../lib/queries';

function benefitLabel(value: string): string {
  return COMMON_BENEFITS.includes(value as (typeof COMMON_BENEFITS)[number])
    ? value.replace(/-/g, ' ').replace(/^\w/, (character) => character.toUpperCase())
    : value;
}

/** "Plus equity, 0.05% – 0.12%" — only when the listing published a band. */
function equityLabel(compensation: JobDetail['job']['compensation']): string | undefined {
  if (compensation === undefined) return undefined;
  const { equityMinPct, equityMaxPct } = compensation;
  if (equityMinPct === undefined && equityMaxPct === undefined) return undefined;
  if (equityMinPct !== undefined && equityMaxPct !== undefined && equityMinPct !== equityMaxPct) {
    return `Plus equity, ${equityMinPct}% – ${equityMaxPct}%`;
  }
  return `Plus equity, ${equityMinPct ?? equityMaxPct}%`;
}

function hostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export function JobPage(): ReactNode {
  const params = useParams<{ id: string }>();
  const query = useJob(params.id);

  if (query.isLoading) {
    return (
      <div className="inner" style={{ paddingTop: 32 }}>
        <BoardSkeleton rows={4} />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="inner" style={{ paddingTop: 32, paddingBottom: 80 }}>
        {isMissing(query.error) ? (
          <MissingState what="listing" />
        ) : (
          <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
        )}
      </div>
    );
  }
  if (query.data === undefined) return null;

  const { job, company, ranks, bidHistory } = query.data;
  const category = getCategory(job.category);
  const subcategory = getSubcategory(job.category, job.subcategory);
  const expired = isExpired(job);
  const salary = compensationLabel(job.compensation);
  const daysLeft = daysRemaining(job.validThrough);
  const equity = equityLabel(job.compensation);
  const firstPoint = bidHistory[0];
  const lastPoint = bidHistory[bidHistory.length - 1];

  return (
    <>
      <div className="inner" style={{ paddingTop: 26 }}>
        <nav className="crumbs" aria-label="Breadcrumb">
          <Link to="/">Leaderboard</Link>
          {category === undefined ? null : (
            <>
              <span className="sep">/</span>
              <Link to={`/c/${category.slug}`}>{category.name}</Link>
            </>
          )}
          {category === undefined || subcategory === undefined ? null : (
            <>
              <span className="sep">/</span>
              <Link to={`/c/${category.slug}/${subcategory.slug}`}>{subcategory.name}</Link>
            </>
          )}
          <span className="sep">/</span>
          <span>{job.title}</span>
        </nav>
      </div>

      <div className="inner split" style={{ paddingTop: 22, paddingBottom: 64 }}>
        <div>
          <header className="jobhead">
            <div className="row" style={{ gap: 10 }}>
              {ranks.global > 0 ? (
                <span className="badge badge-indigo">
                  <LeadCaret colour="#4434d4" />#{ranks.global} globally
                </span>
              ) : null}
              {expired ? (
                <span className="badge badge-caution">Expired</span>
              ) : (
                <span className="badge badge-ok">Live</span>
              )}
              {expired ? null : (
                <span className="badge badge-neutral">
                  Expires in {daysLeft} {daysLeft === 1 ? 'day' : 'days'}
                </span>
              )}
            </div>

            <h1>{job.title}</h1>

            <div className="facts">
              <Link className="company" to={`/companies/${company.slug}`}>
                {company.name}
              </Link>
              {[
                locationLabel(job.locations, job.workplaceType),
                // A remote listing already reads "Remote — <place>", so naming
                // the workplace again would print "Remote · Remote".
                job.workplaceType === 'REMOTE' ? undefined : workplaceLabel(job.workplaceType),
                employmentLabel(job.employmentType),
                seniorityLabel(job.seniority),
              ]
                .filter((fact) => fact !== undefined && fact !== '')
                .map((fact) => (
                  <span key={fact} style={{ display: 'contents' }}>
                    <span className="sep">·</span>
                    <span>{fact}</span>
                  </span>
                ))}
            </div>
          </header>

          <div className="card is-flat jobsummary">
            <div>
              <span className="eyebrow">Salary range</span>
              <div className="value is-money money">
                {salaryFiguresOnly(salary) ?? 'On application'}
              </div>
              <div className="note">
                {[salaryPeriodOnly(salary), equity].filter((part) => part !== undefined).join(' · ')}
              </div>
            </div>
            <div>
              <span className="eyebrow">Employment</span>
              <div className="value">{employmentLabel(job.employmentType)}</div>
              <div className="note">
                {[
                  `${job.openings} ${job.openings === 1 ? 'opening' : 'openings'}`,
                  job.visaSponsorship === undefined
                    ? undefined
                    : VISA_SPONSORSHIP_LABELS[job.visaSponsorship],
                ]
                  .filter((part) => part !== undefined && part !== '')
                  .join(' · ')}
              </div>
            </div>
            <div>
              <span className="eyebrow">Level</span>
              <div className="value">{seniorityLabel(job.seniority)}</div>
              {job.experienceYearsMin === undefined ? null : (
                <div className="note">{job.experienceYearsMin}+ years of experience</div>
              )}
            </div>
          </div>

          {expired ? (
            <div className="notice is-caution" style={{ marginTop: 24 }}>
              <span>
                This listing expired and is no longer ranked. Check with {company.name} before
                applying.
              </span>
            </div>
          ) : null}

          {/*
            The description is stored sanitised: the API reduces submitted markup
            to an allow-list of formatting tags at write time, so what arrives
            here carries no scripts, styles, or attributes.
          */}
          <div className="jd" dangerouslySetInnerHTML={{ __html: job.descriptionHtml }} />

          {job.skills === undefined || job.skills.length === 0 ? null : (
            <section style={{ marginTop: 30 }}>
              <h3>Skills</h3>
              <ul className="taglist">
                {job.skills.map((skill) => (
                  <li key={skill}>{skill}</li>
                ))}
              </ul>
            </section>
          )}

          {job.benefits === undefined || job.benefits.length === 0 ? null : (
            <section style={{ marginTop: 24 }}>
              <h3>Benefits</h3>
              <ul className="taglist">
                {job.benefits.map((benefit) => (
                  <li key={benefit}>{benefitLabel(benefit)}</li>
                ))}
              </ul>
            </section>
          )}

          <div className="card is-flat applybar">
            <div>
              <div className="title">{company.name} handles applications</div>
              <p>We never take applications. This button opens their own posting.</p>
            </div>
            <ApplyButton
              jobId={job.id}
              applyUrl={job.applyUrl}
              companyName={company.name}
              label="Apply on company site"
              compact
            />
          </div>
        </div>

        <aside className="rail">
          <section className="card is-raised is-padded paidcard">
            <span className="eyebrow">Amount paid</span>
            <div className="amount">
              <Money cents={job.totalPaidCents} size="display" />
            </div>
            <p>
              {company.name} has paid <Money cents={job.totalPaidCents} className="mono" />
              {ranks.global > 0 ? <> to hold #{ranks.global}</> : ' on this listing'}. Cumulative,
              public, non-refundable.
            </p>
            <ApplyButton
              jobId={job.id}
              applyUrl={job.applyUrl}
              companyName={company.name}
              label="Apply on company site"
            />
            <p className="destination">Opens {hostname(job.applyUrl)}</p>
          </section>

          <section className="card is-padded sparkcard">
            <div className="head">
              <span className="eyebrow">Bid history</span>
              <span className="small faint">
                {bidHistory.length} {bidHistory.length === 1 ? 'bid' : 'bids'}
                {/* "since" takes a point in time, not a duration. */}
                {job.firstPaidAt === undefined ? '' : ` · since ${formatDate(job.firstPaidAt)}`}
              </span>
            </div>
            <BidSparkline history={bidHistory} />
            {firstPoint === undefined || lastPoint === undefined ? null : bidHistory.length ===
              1 ? (
              // One bid has no span to label. Printing the same figure at both
              // ends reads as two bids under a heading that counts one.
              <div className="axis mono is-single">
                <span>
                  <Money cents={firstPoint.totalCents} /> · {formatShortDate(firstPoint.at)}
                </span>
              </div>
            ) : (
              <div className="axis mono">
                <span>
                  <Money cents={firstPoint.totalCents} /> · {formatShortDate(firstPoint.at)}
                </span>
                <span>
                  <Money cents={lastPoint.totalCents} /> · {formatShortDate(lastPoint.at)}
                </span>
              </div>
            )}
          </section>

          {ranks.global === 0 ? null : (
          <section className="card is-padded">
            <span className="eyebrow">Position on every board</span>
            <div className="rankgrid">
              <div>
                <div className="value mono">#{ranks.global}</div>
                <div className="label">Global</div>
              </div>
              <div>
                <div className="value mono">#{ranks.category}</div>
                <div className="label">{category?.name ?? 'Category'}</div>
              </div>
              <div>
                <div className="value mono">#{ranks.subcategory}</div>
                <div className="label">{subcategory?.name ?? 'Specialism'}</div>
              </div>
            </div>
          </section>
          )}

          <section className="card is-padded">
            <span className="eyebrow">Views and clicks</span>
            <div className="rankgrid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <div>
                <div className="value mono">{job.viewCount.toLocaleString('en-US')}</div>
                <div className="label">Listing views</div>
              </div>
              <div>
                <div className="value mono">{job.applyClickCount.toLocaleString('en-US')}</div>
                <div className="label">Clicks to {company.name}</div>
              </div>
            </div>
          </section>

          <CompanyCard company={company} />

          <ShareRow
            title={job.title}
            companyName={company.name}
            url={`${window.location.origin}/jobs/${job.id}`}
          />

          <section className="card is-flat" style={{ padding: '18px 22px' }}>
            <div className="meta-row">
              <span className="eyebrow">Posted</span>
              <span style={{ fontSize: 13.5 }}>
                {formatDate(job.datePosted)} · {timeAgo(job.datePosted)}
              </span>
            </div>
            <div className="meta-row">
              <span className="eyebrow">{expired ? 'Expired' : 'Expires'}</span>
              <span style={{ fontSize: 13.5 }}>
                {formatDate(job.validThrough)}
                {expired ? '' : ` · ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`}
              </span>
            </div>
            <div className="meta-row">
              <span className="eyebrow">Renewal</span>
              <span style={{ fontSize: 13.5, color: 'var(--ink-secondary)', lineHeight: 1.5 }}>
                Listings run {LISTING_LIFETIME_DAYS} days. Renewal is free. The total paid and the
                position carry over.
              </span>
            </div>
          </section>

          {subcategory === undefined || category === undefined ? null : (
            <Link className="pill pill-quiet pill-block" to={`/c/${category.slug}/${subcategory.slug}`}>
              See the {subcategory.name} board
            </Link>
          )}
        </aside>
      </div>
    </>
  );
}
